import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { orgsApi } from '../features/orgs/api.ts';
import type { OrgUserRow, OrgUserPatch } from '../features/users/api.ts';
import { dv, type Role } from '../i18n/dv.ts';
import { displayName } from '../lib/utils.ts';
import { Icon } from '../components/Icon.tsx';

const INVITE_ROLES: Exclude<Role, 'SYS_ADMIN'>[] = [
  'SECRETARY_GENERAL',
  'PRESIDENT',
  'MEMBER',
  'BUREAU_STAFF',
];

const ROLES: Exclude<Role, 'SYS_ADMIN'>[] = ['PRESIDENT', 'VICE_PRESIDENT', 'SECRETARY_GENERAL', 'MEMBER', 'BUREAU_STAFF', 'VIEWER'];

export function AdminOrgDetail(): JSX.Element {
  const { orgId } = useParams({ from: '/admin/orgs/$orgId' });
  const qc = useQueryClient();
  const { data: org } = useQuery({
    queryKey: ['admin', 'orgs', 'detail', orgId],
    queryFn: () => orgsApi.get(orgId),
  });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Exclude<Role, 'SYS_ADMIN'>>('SECRETARY_GENERAL');
  const [sent, setSent] = useState(false);

  const invite = useMutation({
    mutationFn: () => orgsApi.bootstrapOrgAdmin(orgId, email, role),
    onSuccess: () => {
      setSent(true);
      setEmail('');
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: 'ACTIVE' | 'SUSPENDED') => orgsApi.setStatus(orgId, status),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'orgs', 'detail', orgId] }),
  });

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'orgs', 'users', orgId],
    queryFn: () => orgsApi.listUsers(orgId),
  });

  const invalidateUsers = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'orgs', 'users', orgId] });
    void qc.invalidateQueries({ queryKey: ['admin', 'orgs', 'detail', orgId] });
  };

  const updateUser = useMutation({
    mutationFn: (v: { id: string; patch: OrgUserPatch }) =>
      orgsApi.updateUser(orgId, v.id, v.patch),
    onSuccess: invalidateUsers,
  });

  // Per-row designation editing.
  const [desigEditId, setDesigEditId] = useState<string | null>(null);
  const [desigDv, setDesigDv] = useState('');
  const [desigEn, setDesigEn] = useState('');
  const startDesigEdit = (u: OrgUserRow) => {
    setDesigEditId(u.id);
    setDesigDv(u.designationDv ?? '');
    setDesigEn(u.designationEn ?? '');
  };
  const saveDesig = (id: string) => {
    updateUser.mutate(
      { id, patch: { designationDv: desigDv.trim() || null, designationEn: desigEn.trim() || null } },
      { onSuccess: () => setDesigEditId(null) },
    );
  };

  const [resetSentId, setResetSentId] = useState<string | null>(null);
  const resetUser = useMutation({
    mutationFn: (id: string) => orgsApi.resetUserPassword(orgId, id),
    onSuccess: (_d, id) => { setResetSentId(id); setTimeout(() => setResetSentId(null), 4000); },
  });

  return (
    <div className="page space-y-6 lg:space-y-8">
      <Link to="/admin/orgs" className="btn-ghost btn-sm inline-flex items-center gap-1.5">
        <Icon name="arrow_forward" size={18} />
        {dv.common.back}
      </Link>

      {org ? (
        <>
          <header className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{org.name}</h1>
              <p className="text-sm text-muted-foreground mt-1" dir="ltr">
                {org.contactEmail}
              </p>
              <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                <span
                  className={`pill ${org.status === 'ACTIVE' ? 'pill-active' : 'pill-pending'}`}
                >
                  {org.status === 'ACTIVE' ? dv.common.active : dv.common.suspended}
                </span>
                <span>· <span className="tabular" dir="ltr">{org._count.users}</span> {dv.orgs.users}</span>
              </p>
            </div>
            <button
              onClick={() =>
                setStatus.mutate(org.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE')
              }
              disabled={setStatus.isPending}
              className="btn-secondary btn-sm"
            >
              {org.status === 'ACTIVE' ? dv.orgs.suspend : dv.orgs.activate}
            </button>
          </header>

          <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6 space-y-3">
            <h2 className="status-label text-muted-foreground">{dv.orgs.inviteAdmin}</h2>
            <form
              className="flex flex-col gap-2 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                if (email.trim()) invite.mutate();
              }}
            >
              <input
                aria-label={dv.auth.email}
                type="email"
                dir="ltr"
                placeholder={dv.auth.email}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setSent(false); }}
                required
                className="input flex-1"
              />
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as Exclude<Role, 'SYS_ADMIN'>)}
                aria-label={dv.orgs.adminRole}
                className="select sm:w-auto"
              >
                {INVITE_ROLES.map((r) => (
                  <option key={r} value={r}>{dv.roles[r]}</option>
                ))}
              </select>
              <button
                type="submit"
                disabled={invite.isPending || !email.trim()}
                className="btn-primary"
              >
                {dv.users.invite}
              </button>
            </form>
            {sent && (
              <p className="flex items-center gap-1.5 text-sm text-[hsl(var(--on-success-container))]">
                <Icon name="check_circle" size={16} />
                {dv.orgs.inviteSent}
              </p>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="status-label text-muted-foreground">{dv.users.title}</h2>
            <div className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
              {(users ?? []).map((u) => (
                <div
                  key={u.id}
                  className={`flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-4 ${!u.isActive ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                      <Icon name="person" size={22} />
                    </span>
                    <div className="min-w-0">
                    <p className="font-medium">
                      {displayName(u)}
                      {u.isOrgAdmin && (
                        <span className="pill pill-active ms-2">
                          {dv.orgAdminBadge}
                        </span>
                      )}
                      {!u.isActive && (
                        <span className="pill pill-pending ms-2">
                          {dv.common.inactive}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {u.designationDv ?? dv.roles[u.role]}
                      {u.designationEn && (
                        <span className="ms-2 text-xs text-muted-foreground" dir="ltr">{u.designationEn}</span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground" dir="ltr">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={u.role}
                      disabled={updateUser.isPending}
                      onChange={(e) => updateUser.mutate({ id: u.id, patch: { role: e.target.value as Exclude<Role, 'SYS_ADMIN'> } })}
                      className="select w-auto px-2 py-1"
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{dv.roles[r]}</option>)}
                    </select>
                    <button
                      onClick={() => (desigEditId === u.id ? setDesigEditId(null) : startDesigEdit(u))}
                      className="btn-secondary btn-sm"
                    >
                      {dv.users.editDesignation}
                    </button>
                    <button
                      onClick={() => updateUser.mutate({ id: u.id, patch: { isOrgAdmin: !u.isOrgAdmin } })}
                      className="btn-secondary btn-sm"
                    >
                      {dv.users.orgAdmin}: {u.isOrgAdmin ? dv.common.active : dv.common.none}
                    </button>
                    <button
                      onClick={() => updateUser.mutate({ id: u.id, patch: { isActive: !u.isActive } })}
                      className="btn-secondary btn-sm"
                    >
                      {u.isActive ? dv.users.deactivate : dv.users.activate}
                    </button>
                    <button
                      onClick={() => { if (window.confirm(dv.users.sendReset)) resetUser.mutate(u.id); }}
                      className="btn-secondary btn-sm"
                    >
                      {dv.users.sendReset}
                    </button>
                    {resetSentId === u.id && (
                      <span className="flex items-center gap-1 text-sm text-[hsl(var(--on-success-container))]">
                        <Icon name="check_circle" size={16} />
                        {dv.users.resetSent}
                      </span>
                    )}
                  </div>
                  {desigEditId === u.id && (
                    <div className="flex w-full flex-wrap items-end gap-3 border-t border-black/[0.05] pt-3">
                      <div className="space-y-1">
                        <label className="field-label">{dv.users.designationDv}</label>
                        <input
                          value={desigDv}
                          maxLength={120}
                          onChange={(e) => setDesigDv(e.target.value)}
                          className="input"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="field-label">{dv.users.designationEn}</label>
                        <input
                          dir="ltr"
                          value={desigEn}
                          maxLength={120}
                          onChange={(e) => setDesigEn(e.target.value)}
                          className="input"
                        />
                      </div>
                      <button
                        onClick={() => saveDesig(u.id)}
                        disabled={updateUser.isPending}
                        className="btn-primary"
                      >
                        {dv.common.save}
                      </button>
                      <button
                        onClick={() => setDesigEditId(null)}
                        className="btn-secondary"
                      >
                        {dv.common.cancel}
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {(users ?? []).length === 0 && (
                <p className="p-4 text-center text-sm text-muted-foreground">
                  {usersLoading ? dv.common.loading : dv.users.empty}
                </p>
              )}
            </div>
          </section>
        </>
      ) : (
        <p className="status-label text-muted-foreground" role="status" aria-live="polite">
          {dv.common.loading}
        </p>
      )}
    </div>
  );
}
